/**
 * Which repayments a statement import may book, given the bank's reversals.
 *
 * A bank reversal is two or more rows of one statement (or of two statements) that cancel each other:
 * the GHS 1,600 GIP credit FELIXKWAKYE sent, and the `-REVERSAL_` debit that took it back 27 seconds
 * later. The import used to book every credit it could match to a driver the moment the row was
 * inserted, so the reversed GHS 1,600 became a repayment on Felix Kwakye's contract and somebody had
 * to find it and undo it by hand.
 *
 * WHAT DECIDES A REVERSAL IS THE SERVER, NEVER THIS FILE. The rule runs whenever a list is read, so
 * nothing is stored and nothing is linked: `get_transaction_reversals` only reads. It answers with
 * the ids of this batch that are legs of a reversal the lists hide right now, the ids it holds back
 * because their reversal needs a person (a leg is booked, or a group that looks like a reversal does
 * not balance), and a report of what it saw. This module only turns that answer into bookings and
 * into sentences. The one thing it re-derives is the event KEY, and only to group rows the server
 * has ALREADY called reversed, so the summary counts bank events rather than rows (a card reversal
 * is three rows).
 *
 * FAIL CLOSED. With no answer, nothing is booked: a matched credit stays pending, and the sheet
 * offers to ask again. Booking first and asking afterwards is exactly the incident this replaces.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/** A row this import inserted, with the driver match the preview showed for it */
export interface InsertedRow {
  id: number;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  /** The row's fee, 0 when none — a debit leg weighs its amount plus this */
  fee: number;
  currency: string;
  description: string;
  matchedContractId: number | null;
  matchedDriverName: string | null;
}

export type ReversalReason = 'claimed' | 'unbalanced';

export interface ReversalLeg {
  id: number;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  currency: string;
  description: string;
  /** What else the leg is booked as (`get_transaction_link_purposes`, comma-joined when several), or null */
  purpose: string | null;
}

export interface ReversalReport {
  reason: ReversalReason;
  legs: ReversalLeg[];
}

/** `get_transaction_reversals`'s answer */
export interface ReversalAnswer {
  /** Ids of this batch that are legs of a reversal the lists hide, as the table stood at the call */
  reversed_in_batch: number[];
  /** Ids of this batch inside a reported `claimed` or `unbalanced` group */
  withheld: number[];
  /** Every `claimed` group in the table, then the `unbalanced` groups this batch touches */
  reports: ReversalReport[];
}

export interface Booking {
  transaction_id: number;
  contract_id: number;
}

/** One bank event set aside, as far as THIS statement's rows show it */
export interface SetAsideEvent {
  rows: InsertedRow[];
  label: string;
  currency: string;
  amount: number;
  /** Drivers the preview matched a credit of this event to — the bookings that were not made */
  driverNames: string[];
}

export interface BookingPlan {
  bookings: Booking[];
  /** Matched credits waiting on a reversal check that never answered — empty when it answered */
  pending: InsertedRow[];
  /** Matched credits the check answered for and that are not booked (set aside or withheld) */
  heldBack: InsertedRow[];
  setAside: SetAsideEvent[];
  /**
   * Every `claimed` report: a reversed transaction that is still booked, wherever in the table it
   * is and however it came to be booked. It is said at every import until somebody unbooks it.
   */
  flagged: ReversalReport[];
  /** The `unbalanced` reports */
  otherReports: ReversalReport[];
  /** The check never answered */
  checkFailed: boolean;
}

/** A pending credit Retry did not book because it was assigned in the meantime */
export interface SkippedBooking {
  row: InsertedRow;
  /** What it is booked as now (`get_transaction_purposes`, comma-joined when several) */
  purpose: string;
}

/** What the insert loop counted, for the headline */
export interface ImportCounts {
  imported: number;
  rejected: number;
  failed: number;
  /** The first non-reference failure's message */
  failure: string;
  booked: number;
  bookingFailed: number;
}

// ─── Parsing the answer ──────────────────────────────────────────────────────

const REASONS: readonly ReversalReason[] = ['claimed', 'unbalanced'];

function toId(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) ? n : null;
}

function toIds(v: unknown): number[] | null {
  if (!Array.isArray(v)) return null;
  const ids = v.map(toId);
  return ids.every((id): id is number => id !== null) ? ids : null;
}

function toLeg(v: unknown): ReversalLeg | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const id = toId(o.id);
  const amount = typeof o.amount === 'string' ? Number(o.amount) : o.amount;
  // The server sends null for a leg that is not booked; anything else that is not a string is not
  // its answer, and reading it as "not booked" would drop a flag without a sign
  const purpose = typeof o.purpose === 'string' ? o.purpose : o.purpose === null ? null : undefined;
  if (id === null || (o.type !== 'CREDIT' && o.type !== 'DEBIT')) return null;
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  if (purpose === undefined) return null;
  return {
    id,
    type: o.type,
    amount,
    currency: typeof o.currency === 'string' ? o.currency : '',
    description: typeof o.description === 'string' ? o.description : '',
    purpose,
  };
}

/**
 * The RPC's jsonb, checked. An answer that is not the documented shape is no answer — it is treated
 * like a failed call (nothing booked, retry offered), never read as "nothing to set aside".
 */
export function parseReversalAnswer(data: unknown): ReversalAnswer | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  const reversed = toIds(o.reversed_in_batch);
  const withheld = toIds(o.withheld);
  if (!reversed || !withheld || !Array.isArray(o.reports)) return null;

  const reports: ReversalReport[] = [];
  for (const r of o.reports) {
    if (!r || typeof r !== 'object') return null;
    const reason = (r as Record<string, unknown>).reason;
    const rawLegs = (r as Record<string, unknown>).legs;
    if (!REASONS.includes(reason as ReversalReason)) return null;
    // The server always sends a report's legs. A report without any would have nothing to name,
    // and bookings go by `withheld`, not by the reports — so it is dropped, not read as no answer.
    if (!Array.isArray(rawLegs) || rawLegs.length === 0) continue;
    const legs = rawLegs.map(toLeg);
    if (!legs.every((l): l is ReversalLeg => l !== null)) return null;
    reports.push({ reason: reason as ReversalReason, legs });
  }
  return { reversed_in_batch: reversed, withheld, reports };
}

// ─── Planning the bookings ───────────────────────────────────────────────────

export const isMatchedCredit = (r: InsertedRow) => r.type === 'CREDIT' && r.matchedContractId != null;

/**
 * The server's event key, used ONLY to group rows it has already called reversed. GIP and
 * instant-transfer lines share a leading 12–14 digit token; card and ATM reversals repeat the
 * original's description behind `REV `.
 */
function eventKey(description: string): string {
  const lead = description.match(/^(\d{12,14})_/);
  if (lead) return `L:${lead[1]}`;
  return `R:${description.replace(/^REV /, '')}`;
}

/**
 * A short name for a statement line. GT's descriptions open with the bank's token and bury the
 * counterparty mid-line ("091417732115_**\/_GIP_JPAD PRIME LANE LTD,3225001000310,FELIXKWAKYE _REF:…"),
 * so the name is lifted out where the shape is known and the line is cut short where it is not.
 * What trails the name (" _REF:…", " _AMT:…", " -REVERSAL_…") is the bank's bookkeeping, and goes first.
 */
export function describeLine(description: string): string {
  const body = description
    .replace(/^\d{12,14}_+(?:\*\*\/_)?/, '')
    .replace(/^GIP_/, '')
    .replace(/\s+(?:_(?:REF|AMT):|-REVERSAL_).*$/, '')
    .trim();
  // "…LTD,3225001000310,FELIXKWAKYE", "…LTD:3225001000310:JOSEPH BAAKO", "…TRANSFER/3225001000310/BISMARK YEBOAH"
  const party = body.match(/([,:/])\d{6,}\1\s*(.+)$/);
  if (party) return party[2];
  // A GTWorld line names both ends; the one worth naming is whichever is not us
  const gtworld = body.match(/^VIA GTWORLD FROM (.+?) TO (.+?)(?:\s+REF:.*)?$/i);
  if (gtworld) return /^JPAD PRIME LANE\b/i.test(gtworld[1]) ? gtworld[2] : gtworld[1];
  return body.length > 40 ? `${body.slice(0, 40).trimEnd()}…` : body || '(no description)';
}

export function formatMoney(currency: string, amount: number): string {
  const n = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${n}` : n;
}

function groupSetAside(rows: InsertedRow[]): SetAsideEvent[] {
  const byKey = new Map<string, InsertedRow[]>();
  for (const r of rows) {
    const key = r.description ? eventKey(r.description) : `id:${r.id}`;
    const group = byKey.get(key);
    if (group) group.push(r);
    else byKey.set(key, [r]);
  }
  return [...byKey.values()].map(group => {
    const credits = group.filter(r => r.type === 'CREDIT');
    // A balanced event's credits equal its debits with their fees, so either side is the event's
    // size; this statement may hold only one side of it, and then that side is what it has to go on.
    const side = credits.length > 0 ? credits : group;
    // Named by its largest leg: a returned transfer's GHS 10 send-fee credit says nothing about it
    const named = group.reduce((a, b) => (b.amount > a.amount ? b : a));
    return {
      rows: group,
      label: describeLine(named.description),
      currency: group[0].currency,
      amount: side.reduce((s, r) => s + r.amount + (r.type === 'DEBIT' ? r.fee : 0), 0),
      driverNames: [...new Set(group.filter(isMatchedCredit).map(r => r.matchedDriverName ?? `contract ${r.matchedContractId}`))],
    };
  });
}

const claimedReports = (answer: ReversalAnswer) => answer.reports.filter(r => r.reason === 'claimed');

/**
 * A matched credit is booked only when the check answered AND left it out of both lists: not a
 * reversal leg (`reversed_in_batch`), and not part of a reversal that needs a person (`withheld`).
 * The lists are the table as it stood at the call, not anything this import did — another import's
 * rows may be what made this batch's credit a reversal leg, and it is no more bookable for that.
 */
export function planBookings(rows: InsertedRow[], answer: ReversalAnswer | null): BookingPlan {
  const matched = rows.filter(isMatchedCredit);

  if (!answer) {
    return {
      bookings: [], pending: matched, heldBack: [], setAside: [],
      flagged: [], otherReports: [], checkFailed: true,
    };
  }

  const reversed = new Set(answer.reversed_in_batch);
  const withheld = new Set(answer.withheld);
  const blocked = (r: InsertedRow) => reversed.has(r.id) || withheld.has(r.id);

  return {
    bookings: matched.filter(r => !blocked(r)).map(r => ({ transaction_id: r.id, contract_id: r.matchedContractId! })),
    pending: [],
    heldBack: matched.filter(blocked),
    setAside: groupSetAside(rows.filter(r => reversed.has(r.id))),
    flagged: claimedReports(answer),
    otherReports: answer.reports.filter(r => r.reason !== 'claimed'),
    checkFailed: false,
  };
}

/**
 * The plan with its flags taken from the check asked AFTER the bookings went in. Two imports can
 * race over one pair: each asks while the other's leg is not yet booked, and one books its credit
 * into a reversal the other has just completed. Only a question asked after booking sees that, and
 * since `claimed` reports are table-wide the later answer carries every earlier flag too. With no
 * later answer, the first answer's flags stand. A first-answer flag that held a credit back stays
 * even when the later answer has dropped it (somebody unbooked that reversal in between): it is
 * the only thing that says why the credit was not booked.
 */
export function flagAfterBooking(plan: BookingPlan, after: ReversalAnswer | null): BookingPlan {
  if (!after) return plan;
  const later = claimedReports(after);
  const laterIds = new Set(later.flatMap(r => r.legs.map(l => l.id)));
  const heldBack = new Set(plan.heldBack.map(r => r.id));
  const kept = plan.flagged.filter(r =>
    r.legs.some(l => heldBack.has(l.id)) && !r.legs.some(l => laterIds.has(l.id)));
  return { ...plan, flagged: [...later, ...kept] };
}

// ─── Sentences ───────────────────────────────────────────────────────────────

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** `purpose` is the server's list, joined with ', ' when a leg is booked as more than one thing */
function whereToUnbook(purpose: string): string {
  return purpose.split(', ').map(p => (p === 'repayment'
    ? 'unassign it on Repayments → Transactions'
    : `unlink it from its ${p.replace(/_/g, ' ')} record`)).join(' and ');
}

/** The headline the done step and the toast lead with */
export function importHeadline(c: ImportCounts): string {
  // A row the database refused is silently absent from `imported`, so the count has to say so —
  // otherwise the admin reads a short total as a miscount, not a refusal — and it has to say
  // WHICH refusal, because only one of them is fixable from this screen.
  const parts = [`${c.imported} ${plural(c.imported, 'transaction')} imported`];
  if (c.booked > 0) parts.push(`${c.booked} booked to contracts`);
  if (c.rejected > 0) parts.push(`${c.rejected} rejected — reference already exists`);
  if (c.failed > 0) parts.push(`${c.failed} failed — ${c.failure}`);
  if (c.bookingFailed > 0) parts.push(`${c.bookingFailed} ${plural(c.bookingFailed, 'repayment')} could not be booked`);
  return `${parts.join(', ')}.`;
}

/** A leg the bank's own reversal line carries: the `-REVERSAL_` debit, or the `REV ` card refund */
const isReversalLine = (description: string) => /-REVERSAL_/.test(description) || /^REV /.test(description);

/**
 * One line per thing the admin should know, in plain words and with this statement's rows named.
 * Events are counted, not rows. A leg the server reports that this statement did not carry is named
 * from the report's own description. `canRetry` is false once the sheet is closed: there is no
 * Retry button left to point at.
 */
export function describeBookingPlan(plan: BookingPlan, rows: InsertedRow[], canRetry = true): string[] {
  const notes: string[] = [];
  const byId = new Map(rows.map(r => [r.id, r]));
  const heldBack = new Set(plan.heldBack.map(r => r.id));
  const notBookedTo = (id: number) => {
    const r = byId.get(id);
    return r && heldBack.has(id)
      ? ` (not booked to ${r.matchedDriverName ?? `contract ${r.matchedContractId}`})`
      : '';
  };
  const leg = (l: ReversalLeg) => `${describeLine(l.description)} ${formatMoney(l.currency, l.amount)}${notBookedTo(l.id)}`;

  if (plan.checkFailed) {
    // The lists hide a reversal whether or not this check answers, so only the bookings wait on it
    const n = plan.pending.length;
    if (n > 0) {
      notes.push(`${n} matched ${plural(n, 'repayment')} not booked yet — the bank-reversal check did not answer. ${canRetry ? 'Retry booking below, or assign' : 'Assign'} them on Repayments → Transactions.`);
    } else {
      // Its claimed reports are table-wide, so a check that did not answer is a summary that cannot flag
      notes.push('The bank-reversal check did not answer, so this summary does not flag reversed payments that are still booked.');
    }
    return notes;
  }

  if (plan.setAside.length > 0) {
    const n = plan.setAside.length;
    const events = plan.setAside.map(e =>
      `${e.label} ${formatMoney(e.currency, e.amount)}${e.driverNames.length > 0 ? ` (not booked to ${e.driverNames.join(', ')})` : ''}`);
    notes.push(`${n} set aside as ${plural(n, 'a bank reversal', 'bank reversals')}: ${events.join('; ')}.`);
  }

  for (const report of plan.flagged) {
    // Unbooking is all it takes: the lists apply the rule as they are read, so the moment no leg of
    // the reversal is booked, the whole of it is hidden again — no import has to come first
    for (const l of report.legs.filter(x => x.purpose)) {
      // The bank's own reversal line was not itself reversed — it is the reversing half
      const what = isReversalLine(l.description) ? 'is part of a bank reversal' : 'was reversed by the bank';
      notes.push(`${formatMoney(l.currency, l.amount)} ${describeLine(l.description)} ${what} but is still booked (${l.purpose!.replace(/_/g, ' ')}) — ${whereToUnbook(l.purpose!)}; once nothing in this reversal is booked, it leaves the unassigned list by itself.`);
    }
    const left = report.legs.filter(x => !x.purpose && byId.has(x.id));
    if (left.length > 0) notes.push(`Left unassigned with it: ${left.map(leg).join('; ')}.`);
  }

  for (const report of plan.otherReports) {
    // A leg from an earlier import: a reversal the lists hid until now may be what this one unbalanced
    const earlier = report.legs.some(l => !byId.has(l.id))
      ? ' If one of these rows was imported twice, the duplicate has put a reversal from an earlier import back on the unassigned list, and deleting the duplicate hides it again.'
      : '';
    notes.push(`A bank reversal that does not balance was left unassigned: ${report.legs.map(leg).join('; ')}.${earlier}`);
  }

  return notes;
}

/** The pending credits Retry left alone because somebody assigned them after the import */
export function describeSkipped(skipped: SkippedBooking[]): string[] {
  const n = skipped.length;
  if (n === 0) return [];
  const named = skipped.map(s =>
    `${describeLine(s.row.description)} ${formatMoney(s.row.currency, s.row.amount)} (${s.purpose.replace(/_/g, ' ')})`);
  return [`${n} matched ${plural(n, 'repayment')} skipped — already assigned since the import: ${named.join('; ')}.`];
}

/**
 * The warning toast's title, or null for a plain success: a repayment the preview promised was not
 * booked, the check did not answer, or a reversed transaction is still booked.
 */
export function bookingAttention(plan: BookingPlan): string | null {
  if (plan.checkFailed) {
    return plan.pending.length > 0
      ? 'Import complete — repayments not booked yet'
      : 'Import complete — reversal check did not answer';
  }
  if (plan.heldBack.length > 0) return 'Import complete — some repayments not booked';
  if (plan.flagged.length > 0) return 'Import complete — a booked payment was reversed';
  return null;
}
